"use strict";
'use es6';

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.getDefaultEntity = getDefaultEntity;
exports.isCorrectBlockTypeAndSize = isCorrectBlockTypeAndSize;
exports.updateEntity = updateEntity;
exports.deleteEntity = deleteEntity;
exports.deleteImageBlock = deleteImageBlock;
exports.blockKeysAreAdjacent = exports.correlationIdToBlockKey = exports.blockKeyToCorrelationId = void 0;

var _draftJs = require("draft-js");

var _draftExtend = require("draft-extend");

var _constants = require("composer-plugins/lib/constants");

var _alignments = require("rich-text-lib/constants/alignments");

// Converts Draft block key to its identifiable form via [data-offset-key]
var blockKeyToCorrelationId = function blockKeyToCorrelationId(blockKey) {
  return "" + blockKey + _constants.DRAFT_DATA_OFFSET_KEY_SUFFIX;
}; // Converts [data-offset-key] set on block in DOM to block key used to identify it in Draft blockMap


exports.blockKeyToCorrelationId = blockKeyToCorrelationId;

var correlationIdToBlockKey = function correlationIdToBlockKey(correlationId) {
  var matches = /(.*)-\d+-\d+/.exec(correlationId);

  if (matches && matches.length > 1) {
    return matches[1];
  }

  return correlationId;
};

exports.correlationIdToBlockKey = correlationIdToBlockKey;

var blockKeysAreAdjacent = function blockKeysAreAdjacent(contentState, blockKey1, blockKey2) {
  return contentState.getKeyBefore(blockKey1) === blockKey2 || contentState.getKeyAfter(blockKey1) === blockKey2;
};

exports.blockKeysAreAdjacent = blockKeysAreAdjacent;

function getDefaultEntity(block, getEntity) {
  var entityKey = block.getEntityAt(0);

  if (!entityKey) {
    return undefined;
  }

  return getEntity(entityKey);
}

function isCorrectBlockTypeAndSize(block, blockType) {
  var size = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 1;
  var type = block.getType();
  var length = block.getLength();
  return type === blockType && length === size;
}

function updateEntity(entityKey, editorState, data) {
  var changeType = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 'update-entity';
  var forceSelection = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : true;
  var contentState = editorState.getCurrentContent();
  var contentWithUpdatedEntity = contentState.replaceEntityData(entityKey, data);

  var withUpdatedEntity = _draftJs.EditorState.push(editorState, contentWithUpdatedEntity, changeType);

  if (!forceSelection) {
    return withUpdatedEntity;
  }

  return _draftJs.EditorState.forceSelection(withUpdatedEntity, withUpdatedEntity.getSelection());
}

function deleteEntity(entityKey, editorState) {
  var changeType = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'delete-entity';
  var currentContent = editorState.getCurrentContent();

  var key = _draftExtend.pluginUtils.getEntitySelection(editorState, entityKey).getAnchorKey();

  var contentWithoutEntity = currentContent.set('blockMap', currentContent.getBlockMap().delete(key));
  var blockBeforeEntityKey = currentContent.getKeyBefore(key);
  var selection;

  if (!blockBeforeEntityKey) {
    selection = _draftJs.SelectionState.createEmpty(key);
  } else {
    var blockBeforeEntityLength = currentContent.getBlockForKey(blockBeforeEntityKey).getLength();
    selection = _draftJs.SelectionState.createEmpty(blockBeforeEntityKey).set('anchorOffset', blockBeforeEntityLength).set('focusOffset', blockBeforeEntityLength);
  }

  var withoutEntity = _draftJs.EditorState.push(editorState, contentWithoutEntity, changeType);

  return _draftJs.EditorState.forceSelection(withoutEntity, selection);
}

function isImageBlock(block) {
  return block.getType() === 'atomic';
}

function isFloatedImageBlock(block) {
  var blockAlignment = block.getData().get('alignment');
  return isImageBlock(block) && blockAlignment && [_alignments.Alignments.LEFT, _alignments.Alignments.RIGHT].indexOf(blockAlignment) !== -1;
}

function deleteImageBlock(editorState) {
  var selection = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  var currentSelection = selection || editorState.getSelection(); // backspace as normal if there's a highlighted selection or we're not at
  // the starting offset of the block

  if (!currentSelection.isCollapsed() || currentSelection.getAnchorOffset() !== 0) {
    return undefined;
  }

  var currentBlockKey = currentSelection.getAnchorKey();
  var contentState = editorState.getCurrentContent();
  var currentlySelectedBlock = contentState.getBlockForKey(currentBlockKey);
  var blockBeforeCursor = contentState.getBlockBefore(currentBlockKey);

  if (isImageBlock(currentlySelectedBlock)) {
    // don't accidentally prevent deletion if we're at the start of a block
    // that contains an image -- in this case we don't delete what's before the
    // cursor, since it's "on" an image and we delete that first
    var blockMapWithoutCurrentBlock = contentState.getBlockMap().delete(currentBlockKey);

    var contentWithoutCurrentBlock = _draftJs.ContentState.createFromBlockArray(blockMapWithoutCurrentBlock.toArray());

    var newEditorState = _draftJs.EditorState.push(editorState, contentWithoutCurrentBlock, 'backspace-character');

    var precedingBlockSelection = _draftJs.SelectionState.createEmpty(blockBeforeCursor.getKey());

    if (!isImageBlock(blockBeforeCursor)) {
      // want to leave selection at offset 0 for image blocks so they can be deleted
      // with a single backspace; see commit e6bb0cd
      precedingBlockSelection = precedingBlockSelection.merge({
        anchorOffset: blockBeforeCursor.getLength(),
        focusOffset: blockBeforeCursor.getLength()
      });
    }

    return _draftJs.EditorState.forceSelection(newEditorState, precedingBlockSelection);
  }

  if (!blockBeforeCursor || !isFloatedImageBlock(blockBeforeCursor)) {
    // only prevent backspace deletion of blocks outside document flow
    // (LEFT- and RIGHT-aligned images are floated)
    return undefined;
  } // find and backspace on first viable block


  var allBlocks = contentState.getBlockMap().toList();
  var currentIndex = allBlocks.findIndex(function (block) {
    return block.getKey() === currentBlockKey;
  });
  var blocksBeforeCursor = allBlocks.slice(0, currentIndex);
  var targetBlock = blocksBeforeCursor.findLast(function (block) {
    return !isFloatedImageBlock(block);
  });

  if (!targetBlock) {
    return undefined;
  }

  var targetBlockKey = targetBlock.getKey();
  var currentBlock = contentState.getBlockForKey(currentBlockKey);
  var joinedBlock = targetBlock.merge({
    text: "" + targetBlock.getText() + currentBlock.getText(),
    characterList: targetBlock.getCharacterList().concat(currentBlock.getCharacterList())
  });
  var joinedBlockArray = contentState.getBlockMap().set(targetBlockKey, joinedBlock).delete(currentBlockKey).toArray();

  var contentWithJoinedBlock = _draftJs.ContentState.createFromBlockArray(joinedBlockArray);

  var newState = _draftJs.EditorState.push(editorState, contentWithJoinedBlock, 'backspace-character');

  var selectionAtEndOfOriginalBlockText = _draftJs.SelectionState.createEmpty(targetBlockKey).merge({
    anchorOffset: targetBlock.getLength(),
    focusOffset: targetBlock.getLength()
  });

  return _draftJs.EditorState.forceSelection(newState, selectionAtEndOfOriginalBlockText);
}